/**
 * Build and download an Excel (.xlsx) workbook of admin users.
 */
import { FREE_TRIAL_PLAN_NAME } from './subscription.helpers';

function excelDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function planLabel(sub) {
  if (!sub) return 'None';
  const plan = String(sub.planCode || '').toUpperCase();
  const status = String(sub.status || '').toUpperCase();
  if (status === 'TRIAL' || status === 'TRIALING' || plan === 'FREE_TRIAL') {
    return FREE_TRIAL_PLAN_NAME;
  }
  if (plan === 'ANNUAL') return 'Annual Plan';
  if (plan === 'SIX_MONTHS') return 'Six Months Plan';
  if (plan === 'THREE_MONTHS') return 'Three Months Plan';
  return plan || status || 'None';
}

function accessLabel(sub, accountStatus) {
  const acct = String(accountStatus || 'ACTIVE').toUpperCase();
  if (acct !== 'ACTIVE') return 'Inactive';
  if (!sub || !sub.status) return 'Inactive';
  const s = String(sub.status).toUpperCase();
  const now = Date.now();
  if (s === 'ACTIVE') {
    if (!sub.endsAt) return 'Active';
    return new Date(sub.endsAt).getTime() > now ? 'Active' : 'Inactive';
  }
  if (s === 'TRIAL' || s === 'TRIALING') {
    if (!sub.trialEnds) return 'Active';
    return new Date(sub.trialEnds).getTime() > now ? 'Active' : 'Inactive';
  }
  return 'Inactive';
}

function expiryDate(sub) {
  if (!sub || !sub.status) return '';
  const s = String(sub.status).toUpperCase();
  if (s === 'TRIAL' || s === 'TRIALING') return excelDate(sub.trialEnds);
  return excelDate(sub.endsAt);
}

export function usersToExcelRows(users) {
  return (users || []).map((u, index) => {
    const sub = u.subscription;
    return {
      '#': index + 1,
      Name: u.name || '',
      Email: u.email || '',
      'Email Verified': u.emailVerifiedAt ? 'Yes' : 'No',
      Organisation: u.organization || '',
      Country: u.country || '',
      Role: u.role || 'ENGINEER',
      'Account Status': String(u.accountStatus || 'ACTIVE').toUpperCase(),
      'Account Reason': u.accountStatusReason || '',
      Plan: planLabel(sub),
      'Subscription Status': sub ? String(sub.status || '').toUpperCase() : 'NONE',
      Access: accessLabel(sub, u.accountStatus),
      'Joined': excelDate(u.createdAt),
      'Plan Expiry': expiryDate(sub),
      'Trial Ends': excelDate(sub && sub.trialEnds),
      'Paid Ends': excelDate(sub && sub.endsAt),
      'Razorpay Sub ID': (sub && sub.razorpaySubscriptionId) || '',
    };
  });
}

export async function downloadUsersExcel(users, filenamePrefix = 'xlog3d-users') {
  const mod = await import('xlsx');
  const XLSX = mod.default || mod;
  const rows = usersToExcelRows(users);
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Name: '', Email: '' }]);

  // Reasonable column widths for readability in Excel
  sheet['!cols'] = [
    { wch: 5 },
    { wch: 22 },
    { wch: 32 },
    { wch: 14 },
    { wch: 22 },
    { wch: 14 },
    { wch: 12 },
    { wch: 14 },
    { wch: 20 },
    { wch: 28 },
    { wch: 16 },
    { wch: 10 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 24 },
  ];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Users');

  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `${filenamePrefix}-${stamp}.xlsx`;
  XLSX.writeFile(book, filename);
  return filename;
}
